/**
 * Module 3: Guided Setup Checklist
 * 
 * Permanent checklist with completion tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Circle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHECKLIST_ITEMS = [
  { id: 'profile', title: 'Complete Business Profile', description: 'Add your business information' },
  { id: 'team', title: 'Invite Team Members', description: 'Add team members to collaborate' },
  { id: 'providers', title: 'Configure Lead Providers', description: 'Set up lead generation sources' },
  { id: 'leads', title: 'Import First Leads', description: 'Upload or import your lead list' },
  { id: 'audit', title: 'Generate First AI Audit', description: 'Create an AI-powered audit' },
  { id: 'proposal', title: 'Generate First Proposal', description: 'Create your first proposal' },
  { id: 'compliance', title: 'Review Compliance Settings', description: 'Set up suppression lists and consent' },
  { id: 'revenue', title: 'Test Revenue Engine', description: 'Explore lead scoring and intelligence' }
];

const GuidedSetupChecklist = ({ user }) => {
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const loadChecklist = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/client-success/checklist`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setChecklist(data.completed_items || []);
      }
    } catch (error) {
      console.error('Error loading checklist:', error);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  const toggleItem = async (itemId) => {
    const newChecklist = checklist.includes(itemId)
      ? checklist.filter(id => id !== itemId)
      : [...checklist, itemId];
    
    setChecklist(newChecklist);

    try {
      await fetch(`${API}/client-success/checklist`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ completed_items: newChecklist })
      });
    } catch (error) {
      console.error('Error saving checklist:', error);
    }
  };

  const completionPercentage = (checklist.length / CHECKLIST_ITEMS.length) * 100;

  return (
    <div className="space-y-6" data-testid="guided-setup-checklist">
      {/* Progress Card */}
      <Card className="glass-panel border-white/10">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-white/70">Setup Completion</span>
            <span className="text-sm font-semibold text-[var(--gold)]">{Math.round(completionPercentage)}%</span>
          </div>
          <Progress value={completionPercentage} className="h-2" />
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-white/60">{checklist.length} of {CHECKLIST_ITEMS.length} tasks completed</span>
            {completionPercentage === 100 && (
              <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                <CheckCircle2 size={12} className="mr-1" />
                Complete
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Checklist Items */}
      <Card className="glass-panel border-white/10">
        <CardHeader>
          <CardTitle className="text-white/90">Setup Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {CHECKLIST_ITEMS.map((item) => {
              const isCompleted = checklist.includes(item.id);
              return (
                <div
                  key={item.id}
                  className={`flex items-start gap-4 p-4 border rounded-lg transition-all ${
                    isCompleted
                      ? 'border-green-500/30 bg-green-500/10'
                      : 'border-white/10 bg-white/[0.02]'
                  }`}
                  data-testid={`checklist-item-${item.id}`}
                >
                  <button
                    onClick={() => toggleItem(item.id)}
                    className="mt-1 flex-shrink-0"
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="h-5 w-5 text-green-400" />
                    ) : (
                      <Circle className="h-5 w-5 text-white/40" />
                    )}
                  </button>
                  <div className="flex-1">
                    <h4 className={`text-sm font-semibold ${
                      isCompleted ? 'text-green-300 line-through' : 'text-white/90'
                    }`}>
                      {item.title}
                    </h4>
                    <p className="text-xs text-white/60 mt-1">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GuidedSetupChecklist;
